import {
  Button,
  CheckboxFieldController,
  DatePickerFieldController,
  Divider,
  SelectFieldController,
  Stack,
  TextInputFieldController,
} from '@fukuri-admins/ui/common';
import type { FormEventHandler } from 'react';
import { SectionTitle } from './SectionTitle';
import styles from './styles.module.scss';

type SelectOption = {
  value: string;
  label: string;
};

type FeatureFlagOption = {
  value: string;
  label: string;
};

type Props = {
  /** フォーム送信ハンドラ */
  onSubmit: FormEventHandler<HTMLFormElement>;
  /** キャンセルボタンクリックハンドラ */
  onCancel: React.ComponentProps<'button'>['onClick'];
  /** 送信ボタンの無効化フラグ（送信中 or mutation 中） */
  isButtonDisabled: boolean;
  /** 都道府県選択肢 */
  prefectureOptions: SelectOption[];
  /** 取引店舗選択肢 */
  partnerBranchOptions: SelectOption[];
  /** グループ選択肢 */
  groupOptions: SelectOption[];
  /** クライアント機能フラグ選択肢 */
  featureFlagOptions: FeatureFlagOption[];
  /** 契約プラン選択肢 */
  contractPlanOptions: SelectOption[];
  /** 利用終了日の選択不可判定関数（true を返した日付が選択不可になる） */
  endedOnDisabledDate: (date: Date) => boolean;
};

/**
 * 利用企業作成フォーム Presentation（新版）
 *
 * 会社情報 / プラン情報（クライアント機能フラグがある場合のみ表示）/ 担当者情報 / 請求先情報の
 * フォームフィールドを表示する。
 * 選択肢（都道府県 / 取引店舗 / グループ）は Container から受け取る。
 * 取引店舗・グループは選択肢が0件のとき項目自体を非表示にする（PEN-890）。
 * placeholder は Figma（node-id=19882-94629）に準拠。
 * NOTE: 利用開始日フィールドは仕様変更（PEN-893）により削除済み（会社情報セクションの利用開始日）。
 * NOTE: 電話番号・お電話がつながりやすい時間帯（平日）を追加（PEN-891）。
 *   お電話がつながりやすい時間帯の placeholder は Figma（node-id=27346-21920）に準拠。
 * NOTE: 契約プラン・利用開始日・利用終了日をプラン情報セクションに追加（PEN-1258）。
 *   契約プランは必須のため、機能フラグ選択肢が0件のパートナーでもセクション自体は常に表示する。
 *
 * @param onSubmit - フォーム送信ハンドラ
 * @param onCancel - キャンセルボタンクリックハンドラ
 * @param isButtonDisabled - 送信ボタンの無効化フラグ
 * @param prefectureOptions - 都道府県選択肢
 * @param partnerBranchOptions - 取引店舗選択肢
 * @param groupOptions - グループ選択肢
 * @param featureFlagOptions - クライアント機能フラグ選択肢
 * @param contractPlanOptions - 契約プラン選択肢
 * @param endedOnDisabledDate - 利用終了日の選択不可判定関数
 */
export const CreateClientFormPresentation: React.FC<Props> = ({
  onSubmit,
  onCancel,
  isButtonDisabled,
  prefectureOptions,
  partnerBranchOptions,
  groupOptions,
  featureFlagOptions,
  contractPlanOptions,
  endedOnDisabledDate,
}) => (
  <form onSubmit={onSubmit}>
    {/* フォームフィールド群 */}
    <Stack direction='column' gap={32} className={styles.fieldSections}>
      <h2 className={styles.formTitle}>利用企業の新規作成</h2>
      {/* ── 会社情報 ── */}
      <Stack direction='column' gap={24} width={564}>
        <SectionTitle title='会社情報' />
        <Stack direction='column' gap={16}>
          <TextInputFieldController
            name='corporateNumber'
            fieldWrapperProps={{
              labelText: '法人番号',
            }}
            textInput={{ type: 'text', placeholder: '任意の文字列を入力してください' }}
          />
          <TextInputFieldController
            name='name'
            fieldWrapperProps={{
              labelType: 'required',
              labelText: '会社名',
            }}
            textInput={{ type: 'text', placeholder: '例）株式会社福利厚生' }}
          />
          <TextInputFieldController
            name='nameKana'
            fieldWrapperProps={{
              labelText: '会社名（フリガナ）',
            }}
            textInput={{ type: 'text', placeholder: '例）フクリコウセイ' }}
          />
          <TextInputFieldController
            name='representativeName'
            fieldWrapperProps={{
              labelText: '代表者氏名',
            }}
            textInput={{ type: 'text', placeholder: '例）福利 太郎' }}
          />
          <div className={styles.field172}>
            <TextInputFieldController
              name='postalCode'
              fieldWrapperProps={{
                labelText: '郵便番号（ハイフンなし7桁）',
              }}
              textInput={{ type: 'text', placeholder: '例）0000000' }}
            />
          </div>
          {/* 都道府県 + 市区町村 横並び（gap 24px、全体 564px） */}
          <Stack direction='row' gap={24} alignItems='flex-start'>
            <div className={styles.field172}>
              <SelectFieldController
                name='prefectureCode'
                fieldWrapperProps={{
                  labelText: '都道府県',
                }}
                select={{
                  placeholder: '都道府県を選択',
                  options: prefectureOptions,
                }}
              />
            </div>
            <div className={styles.cityField}>
              <TextInputFieldController
                name='city'
                fieldWrapperProps={{
                  labelText: '市区町村',
                }}
                textInput={{ type: 'text', placeholder: '例）○○市○○町' }}
              />
            </div>
          </Stack>
          <TextInputFieldController
            name='streetAddress'
            fieldWrapperProps={{
              labelText: '番地以降',
            }}
            textInput={{ type: 'text', placeholder: '例）１-２-３' }}
          />
          <div className={styles.employeeCountField}>
            <TextInputFieldController
              name='employeeCount'
              fieldWrapperProps={{
                labelType: 'required',
                labelText: '利用予定者数',
              }}
              textInput={{ type: 'number', placeholder: '例）100' }}
            />
          </div>
          {partnerBranchOptions.length > 0 && (
            <SelectFieldController
              name='partnerBranchId'
              fieldWrapperProps={{
                labelType: 'required',
                labelText: '取引店舗',
              }}
              select={{
                placeholder: '選択してください',
                options: partnerBranchOptions,
              }}
            />
          )}
          <TextInputFieldController
            name='externalClientId'
            fieldWrapperProps={{
              labelText: '外部企業ID',
            }}
            textInput={{ type: 'text', placeholder: '任意の文字列を入力してください' }}
          />
          {groupOptions.length > 0 && (
            <SelectFieldController
              name='groupId'
              fieldWrapperProps={{
                labelText: 'グループ',
              }}
              select={{
                placeholder: '選択してください',
                options: groupOptions,
                clearable: true,
              }}
            />
          )}
        </Stack>
      </Stack>

      <Divider />

      {/* ── プラン情報 ── */}
      <Stack direction='column' gap={24} width={564}>
        <SectionTitle title='プラン情報' />
        <Stack direction='column' gap={16}>
          <SelectFieldController
            name='contractPlanId'
            fieldWrapperProps={{
              labelType: 'required',
              labelText: '契約プラン',
            }}
            select={{
              placeholder: '選択してください',
              options: contractPlanOptions,
            }}
          />
          <Stack direction='row' gap={24} alignItems='flex-start'>
            <div className={styles.dateField}>
              <DatePickerFieldController
                name='startedOn'
                fieldWrapperProps={{ labelType: 'required', labelText: '利用開始日' }}
              />
            </div>
            <div className={styles.dateField}>
              <DatePickerFieldController
                name='endedOn'
                fieldWrapperProps={{ labelText: '利用終了日' }}
                datePicker={{ disabledDate: endedOnDisabledDate }}
              />
            </div>
          </Stack>
          {featureFlagOptions.length > 0 && (
            <CheckboxFieldController
              name='clientFeatureFlags'
              options={featureFlagOptions}
              fieldWrapperProps={undefined}
            />
          )}
        </Stack>
      </Stack>

      <Divider />

      {/* ── 担当者情報 ── */}
      <Stack direction='column' gap={24} width={564}>
        <SectionTitle title='担当者情報' />
        <Stack direction='column' gap={16}>
          <TextInputFieldController
            name='contactPersonName'
            fieldWrapperProps={{
              labelType: 'required',
              labelText: '担当者氏名',
            }}
            textInput={{ type: 'text', placeholder: '例）福利 花子' }}
          />
          <TextInputFieldController
            name='contactPersonNameKana'
            fieldWrapperProps={{
              labelType: 'required',
              labelText: '担当者氏名（フリガナ）',
            }}
            textInput={{ type: 'text', placeholder: '例）フクリ ハナコ' }}
          />
          <TextInputFieldController
            name='contactPersonEmail'
            fieldWrapperProps={{
              labelType: 'required',
              labelText: 'メールアドレス',
            }}
            textInput={{ type: 'email', placeholder: '例）fukuri@example.com' }}
          />
          <TextInputFieldController
            name='contactPersonPhone'
            fieldWrapperProps={{
              labelType: 'required',
              labelText: '電話番号',
            }}
            textInput={{ type: 'tel', placeholder: '090-0000-0000' }}
          />
          <TextInputFieldController
            name='contactPreferredTime'
            fieldWrapperProps={{
              labelText: 'お電話がつながりやすい時間帯（平日）',
            }}
            textInput={{ type: 'text', placeholder: '例）10:00〜15:00' }}
          />
        </Stack>
      </Stack>

      <Divider />

      {/* ── 請求先情報 ── */}
      {/*
       * 請求先 3 項目の placeholder は Figma 上で placeholder/value どちらの状態か曖昧。
       * 担当者情報の placeholder と揃える方針で暫定実装。デザイナー確認待ち。
       */}
      <Stack direction='column' gap={24} width={564}>
        <SectionTitle title='請求先情報' />
        <Stack direction='column' gap={16}>
          <TextInputFieldController
            name='billingContactName'
            fieldWrapperProps={{
              labelText: '請求担当者氏名',
            }}
            textInput={{ type: 'text', placeholder: '例）福利 花子' }}
          />
          <TextInputFieldController
            name='billingContactNameKana'
            fieldWrapperProps={{
              labelText: '請求担当者氏名（フリガナ）',
            }}
            textInput={{ type: 'text', placeholder: '例）フクリ ハナコ' }}
          />
          <TextInputFieldController
            name='billingEmail'
            fieldWrapperProps={{
              labelText: '請求書送付先メールアドレス',
            }}
            textInput={{ type: 'email', placeholder: '例）fukuri@example.com' }}
          />
        </Stack>
      </Stack>
    </Stack>

    {/* ボトムアクションバー（Figma: Admin / Parts / BottomActionButton） */}
    <div className={styles.bottomActionBar}>
      <Stack direction='row' gap={16} alignItems='center'>
        <Button type='button' variant='outline' size='md' onClick={onCancel}>
          キャンセル
        </Button>
        <Button type='submit' size='md' disabled={isButtonDisabled}>
          保存
        </Button>
      </Stack>
    </div>
  </form>
);
